import {
	Button,
	ButtonAppearance,
	ButtonColor,
	ButtonSize
} from '@/components/Button'
import { IconName } from '@/components/Icon'

const appearances: ButtonAppearance[] = ['solid', 'ghost', 'subtle', 'stroke']
const colors: ButtonColor[] = [
	'brand',
	'content1',
	'content2',
	'content3',
	'content4',
	'success',
	'warning',
	'danger',
	'note',
	'overlay'
]
const sizes: ButtonSize[] = ['xs', 'sm', 'md', 'lg']
const sampleIcon: IconName = 'Sparkle'

export function ButtonSandbox () {
	return (
		<div className='spaces:flex spaces:flex-col spaces:gap-32'>
			{/* ── Size × Appearance matrix ── */}
			<Section title='Sizes × Appearances (brand)'>
				<table className='spaces:border-collapse'>
					<thead>
						<tr>
							<th className='spaces:text-left spaces:text-12 spaces:text-content-3 spaces:pr-16 spaces:pb-8' />
							{sizes.map(s => (
								<th key={s} className='spaces:text-left spaces:text-12 spaces:text-content-3 spaces:px-8 spaces:pb-8'>
									{s}
								</th>
							))}
						</tr>
					</thead>
					<tbody>
						{appearances.map(a => (
							<tr key={a}>
								<td className='spaces:text-12 spaces:text-content-3 spaces:pr-16 spaces:py-8 spaces:align-middle spaces:font-default'>
									{a}
								</td>
								{sizes.map(s => (
									<td key={s} className='spaces:px-8 spaces:py-8 spaces:align-middle'>
										<Button
											appearance={a}
											size={s}
											label='Label'
											iconLeft={sampleIcon}
											iconRight={sampleIcon}
										/>
									</td>
								))}
							</tr>
						))}
					</tbody>
				</table>
			</Section>

			{/* ── Color × Appearance matrix ── */}
			<Section title='Colors × Appearances (md)'>
				<div className='spaces:overflow-x-auto'>
					<table className='spaces:border-collapse'>
						<thead>
							<tr>
								<th className='spaces:text-left spaces:text-12 spaces:text-content-3 spaces:pr-16 spaces:pb-8' />
								{appearances.map(a => (
									<th key={a} className='spaces:text-left spaces:text-12 spaces:text-content-3 spaces:px-8 spaces:pb-8'>
										{a}
									</th>
								))}
							</tr>
						</thead>
						<tbody>
							{colors.map(c => (
								<tr key={c} className={c === 'overlay' ? 'spaces:bg-content-1' : ''}>
									<td className='spaces:text-12 spaces:text-content-3 spaces:pr-16 spaces:py-8 spaces:align-middle spaces:font-default'>
										{c}
									</td>
									{appearances.map(a => (
										<td key={a} className='spaces:px-8 spaces:py-8 spaces:align-middle'>
											<Button appearance={a} color={c} size='md' label='Label' />
										</td>
									))}
								</tr>
							))}
						</tbody>
					</table>
				</div>
			</Section>

			{/* ── Icon variants ── */}
			<Section title='Icon Variants'>
				<div className='spaces:flex spaces:flex-wrap spaces:items-end spaces:gap-16'>
					{sizes.map(s => (
						<div key={s} className='spaces:flex spaces:flex-col spaces:items-center spaces:gap-4'>
							<span className='spaces:text-10 spaces:text-content-4'>{s} / iconLeft</span>
							<Button size={s} label='Label' iconLeft={sampleIcon} />
						</div>
					))}
					{sizes.map(s => (
						<div key={`r-${s}`} className='spaces:flex spaces:flex-col spaces:items-center spaces:gap-4'>
							<span className='spaces:text-10 spaces:text-content-4'>{s} / iconRight</span>
							<Button size={s} label='Label' iconRight={sampleIcon} />
						</div>
					))}
					{sizes.map(s => (
						<div key={`io-${s}`} className='spaces:flex spaces:flex-col spaces:items-center spaces:gap-4'>
							<span className='spaces:text-10 spaces:text-content-4'>{s} / iconOnly</span>
							<Button size={s} iconOnly={sampleIcon} />
						</div>
					))}
				</div>
			</Section>

			{/* ── Disabled state ── */}
			<Section title='Disabled State'>
				<div className='spaces:flex spaces:flex-wrap spaces:gap-16'>
					{appearances.map(a => (
						<Button key={a} appearance={a} label={a} disabled />
					))}
				</div>
			</Section>

			{/* ── As anchor ── */}
			<Section title='As Anchor (href)'>
				<div className='spaces:flex spaces:flex-wrap spaces:gap-16'>
					<Button href='#' label='Solid link' />
					<Button href='#' appearance='stroke' label='Stroke link' />
					<Button href='#' appearance='ghost' label='Ghost link' />
					<Button href='#' appearance='subtle' label='Subtle link' />
				</div>
			</Section>
		</div>
	)
}

function Section ({
	title,
	children
}: {
	title: string
	children: React.ReactNode
}) {
	return (
		<div className='spaces:flex spaces:flex-col spaces:gap-12'>
			<h3 className='spaces:text-16 spaces:font-semi spaces:text-content-1'>{title}</h3>
			{children}
		</div>
	)
}
